import type {
  FormDefinition,
  FormSubmission,
  AuditEntry,
  CgtFormType,
  CgtFieldType,
  VersionAuditEntry,
  ProcedureType,
  OosSeverity,
  FormCategory,
} from '@/types/cgt'
import type { AuditTrailRow } from '@/types/audit'
import type { SignerOption } from '@/api/signingApi'
import type { ImportOptionSetPayload, OptionSetResolution } from '@/lib/formImportOptionSets'
import { useMockApi } from '@/lib/useMock'
import {
  mockDefinitions,
  mockSubmissions,
  mockAuditEntries,
} from '@/mocks/cgtForms'

import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** A listed submission, with its form's name embedded by the backend. */
export type SubmissionWithFormName = FormSubmission & {
  formDefinition?: { name: string; version?: number } | null
  /** With `patientNames`: the patient's display name, or null when nothing names them. */
  patientName?: string | null
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** A refused request, with the server's machine-readable `code` when it sent one. */
export class FormsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
  }
}

/** The server's answer to a structural edit of a form version that patients use. */
export const isNewVersionRequired = (e: unknown) =>
  e instanceof FormsApiError && e.code === 'NEW_VERSION_REQUIRED'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.headers),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    let code: string | undefined
    try {
      const parsed = JSON.parse(text) as { message?: string | string[]; code?: unknown }
      if (typeof parsed.code === 'string') code = parsed.code
      if (parsed.message) {
        message = Array.isArray(parsed.message)
          ? parsed.message.join(', ')
          : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new FormsApiError(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`, res.status, code)
  }
  return res.json() as Promise<T>
}

// ─── Save-definition payload shape ───────────────────────────────────────────

export type SaveDefinitionPayload = Omit<
  FormDefinition,
  'id' | 'version' | 'createdAt' | 'updatedAt' | 'createdBy'
>

/**
 * Who may sign one signature field. `restricted: false` means no group rule is
 * set, so anyone who can edit the form may.
 */
export interface AllowedSignatories {
  restricted: boolean
  groups: { id: string; name: string }[]
  members: { id: string; name: string }[]
  /** The field's distinct-signer set (z8nrz7dt44), or null when it is in none. */
  distinctSet: {
    name: string
    /** The set's other fields and who signed each, if anyone. */
    fields: { id: string; label: string; signedBy: string | null }[]
    /** Set when fewer people may sign than the set needs distinct signers. */
    shortfall: string | null
  } | null
}

// ─── API client ───────────────────────────────────────────────────────────────

/** One Review field on a submission (GET /form-submissions/:id/reviews). */
export interface ReviewView {
  fieldId: string
  label: string
  helpText: string | null
  status: 'unassigned' | 'assigned' | 'reviewed'
  assignee: { id: string; name: string } | null
  assignedBy: string | null
  assignedAt: string | null
  signedBy: string | null
  signedAt: string | null
  /** True only while the form is completed-and-locked and this review is unsigned. */
  executable: boolean
}

/** One out-of-specification alert on a submission (GET /form-submissions/:id/oos-alerts). */
export interface OosAlertView {
  id: string
  fieldId: string
  fieldLabel: string
  value: unknown
  severity: OosSeverity
  color: string
  entryComment: string | null
  status: 'open' | 'reviewed' | 'auto_closed'
  raisedBy: string
  raisedAt: string
  reviewedBy: string | null
  reviewAction: 'acknowledge' | 'action_taken' | null
  reviewComment: string | null
  reviewedAt: string | null
  closedReason: string | null
  closedAt: string | null
  /** True when the caller may review this alert now. */
  canReview: boolean
}

/** One review assigned to the caller (GET /form-submissions/pending-review). */
export interface PendingReview {
  submissionId: string
  fieldId: string
  fieldLabel: string
  formDefinitionId: string
  formName: string
  formVersion: number
  repeatIndex: number | null
  patientMrn: string | null
  procedureId: string | null
  procedureName: string | null
  workOrderNumber: string | null
  completedBy: string | null
  /** Who completed the form, by name. Absent from a server older than My Work (2026-09-30). */
  completedByName?: string | null
  completedAt: string | null
  assignedAt: string | null
  /** The form is completed and locked, so the review can be signed now. */
  ready: boolean
  signatureMeaning: string
  /** draft / active while the form is filled; pending_review once completed. */
  submissionStatus?: string
  procedureType?: ProcedureType | null
}

/** One version of a form's family, as the Form Builder's version list shows it. */
export interface FormFamilyVersion {
  id: string
  familyId: string
  name: string
  version: number
  versionName: string | null
  active: boolean
  lastVersionUpdateAt: string | null
  updatedAt: string
}

/** What a completion may carry besides its signature (POST /form-submissions/:id/complete). */
export interface CompletionExtras {
  /** Why labels printed for the form's products do not all reconcile (z8nrz7dt48 AC5). */
  labelDiscrepancyReason?: string
}

export const formsApi = {
  // ── Form definitions (builder side) ────────────────────────────────────────

  // `sort` (`<column>:<asc|desc>`) orders version rows server-side before paging (z8nrz7e4h6).
  listDefinitions: (params: { q?: string; category?: string | string[]; skip?: number; take?: number; active?: boolean; sort?: string } = {}): Promise<{ items: FormDefinition[]; total: number }> => {
    if (useMockApi) return Promise.resolve({ items: mockDefinitions, total: mockDefinitions.length })
    const qs = new URLSearchParams()
    qs.set('isTestData', 'false')
    if (params.q) qs.set('q', params.q)
    const cats = Array.isArray(params.category) ? params.category : params.category ? [params.category] : []
    for (const c of cats) qs.append('category', c)
    if (params.skip !== undefined) qs.set('skip', String(params.skip))
    if (params.take !== undefined) qs.set('take', String(params.take))
    if (params.active !== undefined) qs.set('active', String(params.active))
    if (params.sort) qs.set('sort', params.sort)
    const s = qs.toString()
    return request<{ items: FormDefinition[]; total: number }>(`/form-definitions${s ? `?${s}` : ''}`)
  },

  /**
   * Every version of this form's family, newest first — INACTIVE included
   * (z8nrz7dmb1): the Form Builder's version list is a history view.
   */
  listFamilyVersions: (id: string): Promise<FormFamilyVersion[]> => {
    if (useMockApi) {
      const self = mockDefinitions.find(d => d.id === id)
      const familyId = self?.familyId ?? id
      return Promise.resolve(
        mockDefinitions
          .filter(d => (d.familyId ?? d.id) === familyId)
          .sort((a, b) => (b.version ?? 1) - (a.version ?? 1))
          .map(d => ({
            id: d.id,
            familyId,
            name: d.name,
            version: d.version ?? 1,
            versionName: d.versionName ?? null,
            active: d.active !== false,
            lastVersionUpdateAt: d.lastVersionUpdateAt ?? null,
            updatedAt: d.updatedAt,
          })),
      )
    }
    return request<FormFamilyVersion[]>(`/form-definitions/${id}/family-versions`)
  },

  /**
   * Every version of the named forms only (z8nrz7e8kw): what a page needs to
   * resolve the forms its submissions use, without downloading the library.
   * Same test-data rule as `listDefinitions`. Names go in batches so a long
   * list never makes an over-long URL.
   */
  listDefinitionsByName: async (names: string[]): Promise<FormDefinition[]> => {
    if (useMockApi) return mockDefinitions.filter(d => names.includes(d.name))
    const batches: string[][] = []
    for (let i = 0; i < names.length; i += 50) batches.push(names.slice(i, i + 50))
    const pages = await Promise.all(
      batches.map(batch => {
        const qs = new URLSearchParams()
        qs.set('isTestData', 'false')
        for (const n of batch) qs.append('name', n)
        return request<{ items: FormDefinition[]; total: number }>(`/form-definitions?${qs.toString()}`)
      }),
    )
    return pages.flatMap(p => p.items)
  },

  getDefinition: (id: string): Promise<FormDefinition> => {
    if (useMockApi) {
      const found = mockDefinitions.find(d => d.id === id)
      if (!found) return Promise.reject(new Error(`Mock definition ${id} not found`))
      return Promise.resolve(found)
    }
    return request<FormDefinition>(`/form-definitions/${id}`)
  },

  saveDefinition: (def: SaveDefinitionPayload): Promise<FormDefinition> => {
    if (useMockApi) {
      const saved: FormDefinition = {
        ...def,
        id: crypto.randomUUID(),
        version: 1,
        createdBy: 'mock-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockDefinitions.push(saved)
      return Promise.resolve(saved)
    }
    return request<FormDefinition>('/form-definitions', {
      method: 'POST',
      body: JSON.stringify(def),
    })
  },

  // What importing these option sets would do here — reuse, create, or create
  // under a suffixed name (z8nrz7f3qt). Writes nothing; the commit below runs
  // the same resolver, so the preview cannot disagree with it.
  resolveImportOptionSets: (
    optionSets: ImportOptionSetPayload[],
  ): Promise<OptionSetResolution[]> => {
    if (useMockApi) {
      return Promise.resolve(
        optionSets.map(s => ({ ref: s.ref, status: 'missing' as const, name: s.name })),
      )
    }
    return request<OptionSetResolution[]>('/form-definitions/import/option-sets', {
      method: 'POST',
      body: JSON.stringify({ optionSets }),
    })
  },

  // Create a form from a JSON export together with the option sets its
  // dropdowns are bound to, in ONE server transaction (z8nrz7f3qt AC3.3). A
  // field names an imported set by putting its `ref` in `optionSetId`.
  importDefinition: (
    form: SaveDefinitionPayload,
    optionSets: ImportOptionSetPayload[],
  ): Promise<FormDefinition> => {
    if (useMockApi) return formsApi.saveDefinition(form)
    return request<FormDefinition>('/form-definitions/import', {
      method: 'POST',
      body: JSON.stringify({ form, optionSets }),
    })
  },

  // Full in-place edit of an existing form (name, type, layout, fields) — same
  // id/version, no new version created. Used when editing in the builder.
  updateDefinition: (id: string, def: SaveDefinitionPayload): Promise<FormDefinition> => {
    if (useMockApi) {
      const idx = mockDefinitions.findIndex(d => d.id === id)
      if (idx === -1) return Promise.reject(new Error(`Mock definition ${id} not found`))
      const updated: FormDefinition = {
        ...mockDefinitions[idx],
        ...def,
        updatedAt: new Date().toISOString(),
      }
      mockDefinitions[idx] = updated
      return Promise.resolve(updated)
    }
    return request<FormDefinition>(`/form-definitions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(def),
    })
  },

  // Save edits as a NEW signed version of the form's family (86eybvqgn). The
  // prior version row is untouched — templates and submissions keep pointing
  // at it. `version` is auto-proposed server-side (latest + 1) when omitted.
  createDefinitionVersion: (
    id: string,
    payload: SaveDefinitionPayload & {
      version?: number
      versionName?: string
      // Single-use proof the signer re-entered their password; spent server-side.
      // There is deliberately no `signedBy`: the server resolves WHO signed from
      // the authenticated principal, and sending one is now a 400.
      signingToken: string
      reason?: string
    },
  ): Promise<FormDefinition> => {
    if (useMockApi) {
      const source = mockDefinitions.find(d => d.id === id)
      if (!source) return Promise.reject(new Error(`Mock definition ${id} not found`))
      const { version, reason: _reason, ...def } = payload
      const familyId = source.familyId ?? source.id
      const latest = Math.max(...mockDefinitions.filter(d => (d.familyId ?? d.id) === familyId).map(d => d.version))
      const created: FormDefinition = {
        ...source,
        ...def,
        id: crypto.randomUUID(),
        familyId,
        version: version ?? latest + 1,
        active: true,
        createdBy: 'mock-user',
        lastVersionUpdateAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockDefinitions.push(created)
      return Promise.resolve(created)
    }
    return request<FormDefinition>(`/form-definitions/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  // Signed ACTIVE/INACTIVE toggle. The backend rejects with 409 while an
  // ACTIVE procedure template still references the version.
  setDefinitionStatus: (
    id: string,
    payload: { active: boolean; signingToken: string; reason?: string },
  ): Promise<FormDefinition> => {
    if (useMockApi) {
      const found = mockDefinitions.find(d => d.id === id)
      if (!found) return Promise.reject(new Error(`Mock definition ${id} not found`))
      found.active = payload.active
      return Promise.resolve(found)
    }
    return request<FormDefinition>(`/form-definitions/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  },

  // Version-control events for the form's whole family, newest first.
  getDefinitionVersionAudit: (id: string): Promise<VersionAuditEntry[]> => {
    if (useMockApi) return Promise.resolve([])
    return request<VersionAuditEntry[]>(`/form-definitions/${id}/version-audit`)
  },

  // In-place edit of a form's name/type — no new version, fields untouched.
  updateDefinitionMeta: (
    id: string,
    meta: {
      name?: string
      category?: FormDefinition['category']
      allowedActions?: string[]
      // Same null-is-a-real-value rule as crossFormTemplateId below: null means
      // "does not repeat" and is what unchecking `Repeat` sends. The server
      // clears the column rather than trusting the client, but sending it keeps
      // the two halves of the setting in one request.
      defaultRepeatCount?: number | null
      requiresPatientHistory?: boolean
      // null is a real value here ("None"), so it must survive JSON — only an
      // absent key means "leave unchanged".
      crossFormTemplateId?: string | null
    },
  ): Promise<FormDefinition> => {
    if (useMockApi) {
      const found = mockDefinitions.find(d => d.id === id)
      if (!found) return Promise.reject(new Error(`Mock definition ${id} not found`))
      Object.assign(found, meta, { updatedAt: new Date().toISOString() })
      return Promise.resolve(found)
    }
    return request<FormDefinition>(`/form-definitions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(meta),
    })
  },

  // Returns true when the form sits in a procedure-template step that has ≥1
  // live procedure — i.e. patients are currently using it. Used by the form
  // builder to decide whether to save in place or require a new signed version.
  checkInUse: (id: string): Promise<{ inUse: boolean }> => {
    if (useMockApi) return Promise.resolve({ inUse: false })
    return request<{ inUse: boolean }>(`/form-definitions/${id}/in-use`)
  },

  // Procedure templates containing ANY version of this form's family — powers
  // the "from an earlier form" cross-form reference picker.
  listFormProcedureTemplates: (
    id: string,
  ): Promise<{
    items: Array<{
      id: string
      name: string
      version: number | null
      active: boolean
      familyId: string | null
      /** ISO timestamp — orders templates that are in different families. */
      updatedAt: string
      stepId: string
    }>
  }> => {
    if (useMockApi) return Promise.resolve({ items: [] })
    return request(`/form-definitions/${id}/procedure-templates`)
  },

  // Hard delete. The backend rejects with 409 (and an explanatory message)
  // while submissions or procedure sequences still reference the definition.
  deleteDefinition: (id: string): Promise<{ ok: true }> => {
    if (useMockApi) {
      const idx = mockDefinitions.findIndex(d => d.id === id)
      if (idx === -1) return Promise.reject(new Error(`Mock definition ${id} not found`))
      mockDefinitions.splice(idx, 1)
      return Promise.resolve({ ok: true })
    }
    return request<{ ok: true }>(`/form-definitions/${id}`, { method: 'DELETE' })
  },

  // ── Form submissions (renderer / filler side) ───────────────────────────────

  getSubmissionsForPatient: (
    mrn: string,
    procedureId?: string,
    /**
     * `name`: each submission embeds only its form's name, not a copy of the
     * form's fields per submission — for a caller that loads the definitions
     * itself (z8nrz7e8kw). An older backend ignores it.
     */
    definition?: 'name',
  ): Promise<FormSubmission[]> => {
    if (useMockApi) {
      const byMrn = mockSubmissions.filter(s => s.patientMrn === mrn)
      return Promise.resolve(procedureId ? byMrn.filter(s => s.procedureId === procedureId) : byMrn)
    }
    const params = new URLSearchParams({ patientMrn: mrn, isTestData: 'false' })
    if (procedureId) params.append('procedureId', procedureId)
    if (definition) params.append('definition', definition)
    return request<{ items: FormSubmission[] }>(`/form-submissions?${params}`).then(r => r.items)
  },

  /**
   * Every form opened inside one run, with its form name. Keyed on the run
   * alone, so it works for runs with no patient (MES, Inventory, kits).
   */
  listProcedureSubmissions: (procedureId: string): Promise<Array<FormSubmission & { formDefinition?: { name: string } | null }>> => {
    if (useMockApi) {
      return Promise.resolve(mockSubmissions.filter(s => s.procedureId === procedureId))
    }
    const params = new URLSearchParams({ procedureId, isTestData: 'false', take: '200' })
    return request<{ items: Array<FormSubmission & { formDefinition?: { name: string } | null }> }>(
      `/form-submissions?${params}`,
    ).then(r => r.items)
  },

  /** List all submissions (no MRN filter). Optionally search by MRN and paginate. */
  listAllSubmissions: (
    params: {
      q?: string
      skip?: number
      take?: number
      sort?: string
      /**
       * `name`: each submission embeds only its form's name, not the form's
       * fields, for a caller that fetches the definitions itself (z8nrz7e8kw).
       * An older backend ignores it and embeds everything, as before.
       */
      definition?: 'name'
      /** Only submissions of forms with this Form Type (the Collection page). */
      category?: FormCategory
      /** Attach each patient's display name (`patientName`). */
      patientNames?: boolean
    } = {},
  ): Promise<{ items: SubmissionWithFormName[]; total: number }> => {
    if (useMockApi) {
      const all = [...mockSubmissions].reverse()
      // Mock submissions carry no form category, so a category filter matches none.
      const byCategory = params.category ? [] : all
      const filtered = params.q ? byCategory.filter(s => s.patientMrn?.toLowerCase().includes(params.q!.toLowerCase()) ?? false) : byCategory
      const skip = params.skip ?? 0
      const take = params.take ?? 200
      return Promise.resolve({ items: filtered.slice(skip, skip + take), total: filtered.length })
    }
    const qs = new URLSearchParams()
    qs.set('isTestData', 'false')
    if (params.q) qs.set('q', params.q)
    if (params.skip !== undefined) qs.set('skip', String(params.skip))
    if (params.take !== undefined) qs.set('take', String(params.take))
    // Column sort, applied server-side before paging (z8nrz7e4h6).
    if (params.sort) qs.set('sort', params.sort)
    if (params.definition) qs.set('definition', params.definition)
    if (params.category) qs.set('category', params.category)
    if (params.patientNames) qs.set('patientNames', 'true')
    const s = qs.toString()
    return request<{ items: SubmissionWithFormName[]; total: number }>(`/form-submissions${s ? `?${s}` : ''}`)
  },

  /** Get a single submission by ID. Used for standalone /forms/:submissionId route. */
  getSubmission: (id: string): Promise<FormSubmission> => {
    if (useMockApi) {
      const found = mockSubmissions.find(s => s.id === id)
      if (!found) return Promise.reject(new Error(`Mock submission ${id} not found`))
      return Promise.resolve(found)
    }
    return request<FormSubmission>(`/form-submissions/${id}`)
  },

  createSubmission: (payload: {
    formDefinitionId: string
    /**
     * Omit on a procedure that has no patient — an MES order takes its patient
     * from an Epic order, or never has one (z8nrz7ckj9). Never send an empty
     * string: the server reads a supplied value and rejects a blank one. No
     * SHAPE is required of it — that rule went in z8nrz7dyc2.
     */
    patientMrn?: string
    formType?: CgtFormType
    /** When set, the predecessor gate uses this template's sequence. */
    procedureTemplateId?: string
    /** Live procedure — links submission and resolves the template when omitted. */
    procedureId?: string
    /** True when the form is added on the fly (bypasses predecessor gate). */
    isAdHoc?: boolean
    /**
     * Which repeat of a repeating step to start (1-based).
     *
     * Omit to let the server start the lowest repeat not yet begun. The server
     * REJECTS this on a step that does not repeat, so never default it to 1 —
     * absent and 1 are different requests.
     */
    repeatIndex?: number
    /** The procedure-template sequence step from which this ad-hoc form was added. */
    addedFromStepId?: string
    /** Keep the ad-hoc form locked until `addedFromStepId` is completed (Upload forms). */
    waitsForAddedFromStep?: boolean
  }): Promise<FormSubmission> => {
    if (useMockApi) {
      const submission: FormSubmission = {
        id: crypto.randomUUID(),
        formDefinitionId: payload.formDefinitionId,
        formType: payload.formType ?? 'chart',
        patientMrn: payload.patientMrn ?? null,
        isAdHoc: payload.isAdHoc,
        addedFromStepId: payload.addedFromStepId,
        waitsForAddedFromStep: payload.waitsForAddedFromStep,
        status: 'draft',
        values: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockSubmissions.push(submission)
      return Promise.resolve(submission)
    }
    return request<FormSubmission>('/form-submissions', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  saveFieldValue: (
    submissionId: string,
    fieldId: string,
    value: unknown,
    reason?: string,
    // Required only when the field is a SIGNATURE — the server knows which
    // fields those are and refuses one without a token. Ordinary field saves
    // leave it undefined.
    signingToken?: string,
    // Stored with an out-of-specification value; required by the server when
    // the value falls in a comment-required band (z8nrz7eq4e).
    comment?: string,
  ): Promise<{ submission: FormSubmission; auditEntry: AuditEntry }> => {
    if (useMockApi) {
      const idx = mockSubmissions.findIndex(s => s.id === submissionId)
      if (idx === -1) return Promise.reject(new Error(`Mock submission ${submissionId} not found`))
      const old = mockSubmissions[idx]
      const oldValue = old.values[fieldId]
      const updated: FormSubmission = {
        ...old,
        values: { ...old.values, [fieldId]: value },
        updatedAt: new Date().toISOString(),
      }
      mockSubmissions[idx] = updated
      const auditEntry: AuditEntry = {
        id: crypto.randomUUID(),
        submissionId,
        fieldId,
        fieldLabel: fieldId,
        oldValue,
        newValue: value,
        changedBy: 'mock-user',
        changedAt: new Date().toISOString(),
        reason,
      }
      mockAuditEntries.push(auditEntry)
      return Promise.resolve({ submission: updated, auditEntry })
    }
    return request<{ submission: FormSubmission; auditEntry: AuditEntry }>(
      `/form-submissions/${submissionId}/fields/${fieldId}`,
      { method: 'PATCH', body: JSON.stringify({ value, reason, signingToken, comment }) },
    )
  },

  completeSubmission: (
    submissionId: string,
    // Single-use proof the signer re-entered their password. The mock path
    // ignores it; the server spends it before the completion is applied.
    // The signer is resolved server-side from the authenticated principal.
    signingToken: string,
    // Anything the completion carries besides the signature — today the reason
    // printed labels do not reconcile (z8nrz7dt48), signed by this completion.
    extras: CompletionExtras = {},
  ): Promise<FormSubmission> => {
    if (useMockApi) {
      const idx = mockSubmissions.findIndex(s => s.id === submissionId)
      if (idx === -1) return Promise.reject(new Error(`Mock submission ${submissionId} not found`))
      const updated: FormSubmission = {
        ...mockSubmissions[idx],
        status: 'completed',
        submittedBy: 'mock-user',
        submittedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockSubmissions[idx] = updated
      return Promise.resolve(updated)
    }
    return request<FormSubmission>(`/form-submissions/${submissionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ signingToken, ...extras }),
    })
  },

  /**
   * Remove one applied signature from a field (z8nrz7dt40), without reopening
   * the whole form. Password-verified — the token names who is removing it,
   * which for "Remove as:" is the picked person — and the reason is required.
   * Refused on a completed form: unlock it first.
   */
  removeSignature: (
    submissionId: string,
    fieldId: string,
    signingToken: string,
    reason: string,
  ): Promise<FormSubmission> => {
    if (useMockApi) {
      const idx = mockSubmissions.findIndex(s => s.id === submissionId)
      if (idx === -1) return Promise.reject(new Error(`Mock submission ${submissionId} not found`))
      const values = { ...mockSubmissions[idx].values }
      delete values[fieldId]
      mockSubmissions[idx] = { ...mockSubmissions[idx], values, updatedAt: new Date().toISOString() }
      return Promise.resolve(mockSubmissions[idx])
    }
    return request<FormSubmission>(
      `/form-submissions/${submissionId}/fields/${fieldId}/remove-signature`,
      { method: 'POST', body: JSON.stringify({ signingToken, reason }) },
    )
  },

  /**
   * The "Sign as:" / "Remove as:" dropdown for one action on a submission:
   * active people, alphabetical, each marked eligible or not by the same rule
   * the server applies when the signature is given.
   */
  listSubmissionSigners: (
    submissionId: string,
    query: { action: 'sign' | 'remove' | 'complete'; fieldId?: string },
  ): Promise<SignerOption[]> => {
    if (useMockApi) return Promise.resolve([])
    const qs = new URLSearchParams({ action: query.action })
    if (query.fieldId) qs.set('fieldId', query.fieldId)
    return request<SignerOption[]>(`/form-submissions/${submissionId}/signers?${qs}`)
  },

  /** "View Allowed Signatories": the groups and people who may sign a field. */
  getAllowedSignatories: (
    submissionId: string,
    fieldId: string,
  ): Promise<AllowedSignatories> => {
    if (useMockApi) return Promise.resolve({ restricted: false, groups: [], members: [], distinctSet: null })
    return request<AllowedSignatories>(
      `/form-submissions/${submissionId}/fields/${fieldId}/allowed-signatories`,
    )
  },

  /**
   * Unlock a completed submission (completed → active) so it can be corrected
   * and completed again. Signed like completion, and the reason is required:
   * the server records both in the form's audit trail.
   */
  unlockSubmission: (
    submissionId: string,
    signingToken: string,
    reason: string,
  ): Promise<FormSubmission> => {
    if (useMockApi) {
      const idx = mockSubmissions.findIndex(s => s.id === submissionId)
      if (idx === -1) return Promise.reject(new Error(`Mock submission ${submissionId} not found`))
      if (mockSubmissions[idx].status !== 'completed') {
        return Promise.reject(new Error('Only a completed form can be unlocked'))
      }
      const updated: FormSubmission = {
        ...mockSubmissions[idx],
        status: 'active',
        submittedBy: undefined,
        submittedAt: undefined,
        updatedAt: new Date().toISOString(),
      }
      mockSubmissions[idx] = updated
      return Promise.resolve(updated)
    }
    return request<FormSubmission>(`/form-submissions/${submissionId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ signingToken, reason }),
    })
  },

  // ── Skip / unskip a form of a run ─────────────────────────────────────────
  // Left unfilled on purpose from the form's "+" menu: read-only, and done for
  // the run so the next form opens. Unskip opens it to be filled after all.

  /** Skip a form already opened in its run; the reason goes on its audit trail. */
  skipSubmission: (submissionId: string, reason: string): Promise<FormSubmission> =>
    request<FormSubmission>(`/form-submissions/${submissionId}/skip`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Skip a form of a run nobody has opened yet: the same payload as starting
   * it, so the server holds it to the same rules (step unlocked, repeat valid).
   */
  skipNewSubmission: (payload: {
    formDefinitionId: string
    patientMrn?: string
    procedureTemplateId?: string
    procedureId: string
    repeatIndex?: number
    /** Required: goes on the form's audit trail. */
    reason: string
  }): Promise<FormSubmission> =>
    request<FormSubmission>('/form-submissions/skip', { method: 'POST', body: JSON.stringify(payload) }),

  /** Which product(s) of the procedure this form is about (z8nrz7fxub). Audited; `[]` clears. */
  setWorkingProducts: (submissionId: string, productIds: string[]): Promise<FormSubmission> =>
    request<FormSubmission>(`/form-submissions/${submissionId}/working-products`, {
      method: 'PUT',
      body: JSON.stringify({ productIds }),
    }),

  /** Open a skipped form again, to be filled. */
  unskipSubmission: (submissionId: string): Promise<FormSubmission> =>
    request<FormSubmission>(`/form-submissions/${submissionId}/unskip`, { method: 'POST' }),

  // ── Review fields (z8nrz7dt41) ─────────────────────────────────────────────
  // A Review field is assigned while the form is filled and e-signed by its
  // assignee once the form is completed and locked. Never by a performer.

  /** The form's Review area: each Review field's assignee, status and signature. */
  listReviews: (submissionId: string): Promise<ReviewView[]> => {
    if (useMockApi) return Promise.resolve([])
    return request<ReviewView[]>(`/form-submissions/${submissionId}/reviews`)
  },

  /** Who may be assigned: review-permission holders, minus anyone who performs the step. */
  listReviewAssignees: (submissionId: string, fieldId: string): Promise<SignerOption[]> => {
    if (useMockApi) return Promise.resolve([])
    return request<SignerOption[]>(
      `/form-submissions/${submissionId}/reviews/${encodeURIComponent(fieldId)}/assignees`,
    )
  },

  assignReview: (submissionId: string, fieldId: string, assigneeId: string | null): Promise<ReviewView> =>
    request<ReviewView>(`/form-submissions/${submissionId}/reviews/${encodeURIComponent(fieldId)}/assignee`, {
      method: 'PUT',
      body: JSON.stringify({ assigneeId }),
    }),

  /**
   * E-sign a Review field. `completedAt` is the version the reviewer read: the
   * server refuses if the form was reopened or re-completed since. The last
   * review signed completes the form.
   */
  signReview: (
    submissionId: string,
    fieldId: string,
    signingToken: string,
    completedAt: string,
  ): Promise<{ review: ReviewView; submissionStatus: FormSubmission['status'] }> =>
    request(`/form-submissions/${submissionId}/reviews/${encodeURIComponent(fieldId)}/sign`, {
      method: 'POST',
      body: JSON.stringify({ signingToken, completedAt }),
    }),

  /** The form's out-of-specification alerts, newest first (z8nrz7eq4e). */
  listOosAlerts: (submissionId: string): Promise<OosAlertView[]> => {
    if (useMockApi) return Promise.resolve([])
    return request<OosAlertView[]>(`/form-submissions/${submissionId}/oos-alerts`)
  },

  /** Acknowledge an alert, or record the action taken (comment required). */
  reviewOosAlert: (
    submissionId: string,
    alertId: string,
    action: 'acknowledge' | 'action_taken',
    comment?: string,
  ): Promise<OosAlertView> =>
    request<OosAlertView>(`/form-submissions/${submissionId}/oos-alerts/${alertId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action, comment }),
    }),

  /** Adds or replaces the comment on an open alert's value. */
  setOosAlertComment: (submissionId: string, alertId: string, comment: string): Promise<OosAlertView> =>
    request<OosAlertView>(`/form-submissions/${submissionId}/oos-alerts/${alertId}/comment`, {
      method: 'PATCH',
      body: JSON.stringify({ comment }),
    }),

  /** The caller's review worklist: reviews assigned to them, ready-to-sign first. */
  pendingReviews: (): Promise<PendingReview[]> => {
    if (useMockApi) return Promise.resolve([])
    return request<PendingReview[]>('/form-submissions/pending-review')
  },

  /** How many of the worklist's reviews can be signed now: the top bar's badge (z8nrz7fxhr). */
  pendingReviewCount: (): Promise<{ ready: number }> => {
    if (useMockApi) return Promise.resolve({ ready: 0 })
    return request<{ ready: number }>('/form-submissions/pending-review/count')
  },

  /**
   * The submission's whole audit trail, as one feed.
   *
   * The endpoint merges the 21 CFR Part 11 form trail with the product trail
   * server-side and returns them in one shape, newest first — see
   * `src/types/audit.ts`. There is no mock branch: an audit trail is the one
   * screen where invented rows are worse than an empty table, because nothing
   * on it distinguishes a fixture from a record of what somebody did.
   */
  getAuditTrail: (submissionId: string): Promise<AuditTrailRow[]> =>
    request<AuditTrailRow[]>(`/form-submissions/${submissionId}/audit`),

  /**
   * A patient's whole audit trail (z8nrz7dq6q): every audited change on their
   * forms, products and process steps, merged newest first, same row shape.
   */
  getPatientAuditTrail: (patientMrn: string): Promise<AuditTrailRow[]> =>
    request<AuditTrailRow[]>(`/form-submissions/audit?patientMrn=${encodeURIComponent(patientMrn)}`),
}

// Re-export the field type map for callers that need it
export type { CgtFieldType }
